/**
 * Post code validation utility for UK and Bangladesh
 * Uses web APIs for real-time validation
 */

import axios from 'axios';

/**
 * Validates UK post code format (regex check)
 * UK post codes can have various formats:
 * - SW1A 1AA (area + district + sector + unit)
 * - M1 1AA (short format)
 * - B33 8TH
 * - W1A 0AX
 * - EC1A 1BB
 * 
 * General pattern: [A-Z]{1,2}[0-9][A-Z0-9]? [0-9][A-Z]{2}
 */
export const validateUKPostCodeFormat = (postCode) => {
  if (!postCode || typeof postCode !== 'string') return false;
  
  // Remove all spaces and convert to uppercase
  const cleaned = postCode.replace(/\s+/g, '').toUpperCase();
  
  // UK post code regex pattern
  // Matches formats like: SW1A1AA, M11AA, B338TH, W1A0AX, EC1A1BB
  const ukPostCodePattern = /^[A-Z]{1,2}[0-9][A-Z0-9]?[0-9][A-Z]{2}$/;
  
  return ukPostCodePattern.test(cleaned);
};

/**
 * Validates UK post code using postcodes.io API (free, no API key required)
 * @param {string} postCode - The post code to validate
 * @returns {Promise<Object>} { isValid: boolean, error: string|null, data: object|null }
 */
export const validateUKPostCodeFromWeb = async (postCode) => {
  if (!postCode || typeof postCode !== 'string') {
    return { isValid: false, error: 'Post code is required', data: null };
  }

  // First check format
  if (!validateUKPostCodeFormat(postCode)) {
    return {
      isValid: false,
      error: 'Invalid UK post code format. Example: SW1A 1AA, M1 1AA, B33 8TH',
      data: null
    };
  }

  try {
    // Clean the postcode for API call
    const cleaned = postCode.replace(/\s+/g, '').toUpperCase();
    
    // Use postcodes.io API (free, no API key required)
    const response = await axios.get(`https://api.postcodes.io/postcodes/${cleaned}`, {
      timeout: 5000 // 5 second timeout
    });

    if (response.data && response.data.status === 200 && response.data.result) {
      return {
        isValid: true,
        error: null,
        data: {
          postcode: response.data.result.postcode,
          country: response.data.result.country,
          region: response.data.result.region,
          district: response.data.result.admin_district,
          ward: response.data.result.admin_ward,
          latitude: response.data.result.latitude,
          longitude: response.data.result.longitude
        }
      };
    } else {
      return {
        isValid: false,
        error: 'Post code not found. Please check and try again.',
        data: null
      };
    }
  } catch (error) {
    // If API fails, fall back to format validation
    if (error.response && error.response.status === 404) {
      return {
        isValid: false,
        error: 'Post code not found. Please check and try again.',
        data: null
      };
    }
    
    // Network error or timeout - fall back to format validation
    console.warn('Postcode API error, using format validation:', error.message);
    return {
      isValid: validateUKPostCodeFormat(postCode),
      error: validateUKPostCodeFormat(postCode) ? null : 'Invalid UK post code format',
      data: null,
      fallback: true // Indicates we used format validation instead of API
    };
  }
};

/**
 * Validates Bangladesh post code format
 * Bangladesh post codes are 4 digits (e.g., 1200, 1212, 1000)
 */
export const validateBangladeshPostCode = (postCode) => {
  if (!postCode || typeof postCode !== 'string') return false;
  
  // Remove all spaces
  const cleaned = postCode.replace(/\s+/g, '');
  
  // Bangladesh post code is exactly 4 digits
  const bangladeshPostCodePattern = /^\d{4}$/;
  
  return bangladeshPostCodePattern.test(cleaned);
};

/**
 * Validates Bangladesh post code using web API (if available)
 * Currently uses format validation as there's no free public API
 * @param {string} postCode - The post code to validate
 * @returns {Promise<Object>} { isValid: boolean, error: string|null, data: object|null }
 */
export const validateBangladeshPostCodeFromWeb = async (postCode) => {
  if (!postCode || typeof postCode !== 'string') {
    return { isValid: false, error: 'Post code is required', data: null };
  }

  // Format validation for Bangladesh
  const isValid = validateBangladeshPostCode(postCode);
  
  if (!isValid) {
    return {
      isValid: false,
      error: 'Invalid Bangladesh post code format. Must be 4 digits (e.g., 1200, 1212)',
      data: null
    };
  }

  // Note: There's no free public API for Bangladesh postcodes
  // You could integrate with a paid service or your own database if needed
  return {
    isValid: true,
    error: null,
    data: {
      postcode: postCode,
      country: 'Bangladesh'
    }
  };
};

/**
 * Validates post code based on country (synchronous format check)
 * @param {string} postCode - The post code to validate
 * @param {string|number} countryId - The country ID (22 for UK, need to find Bangladesh ID)
 * @param {Array} countries - Array of country objects with id and name
 * @returns {Object} { isValid: boolean, error: string|null }
 */
export const validatePostCode = (postCode, countryId, countries = []) => {
  if (!postCode || !postCode.trim()) {
    return { isValid: false, error: 'Post code is required' };
  }

  // Find country by ID
  const country = countries.find(c => String(c.id) === String(countryId));
  const countryName = country?.name?.toLowerCase() || '';

  // UK validation (id 22 or name contains "uk")
  if (countryId === 22 || countryId === '22' || countryName === 'uk') {
    if (!validateUKPostCodeFormat(postCode)) {
      return {
        isValid: false,
        error: 'Invalid UK post code format. Example: SW1A 1AA, M1 1AA, B33 8TH'
      };
    }
    return { isValid: true, error: null };
  }

  // Bangladesh validation (check by name or common IDs)
  // You may need to adjust the ID based on your actual data
  if (countryName.includes('bangladesh') || countryName === 'bd') {
    if (!validateBangladeshPostCode(postCode)) {
      return {
        isValid: false,
        error: 'Invalid Bangladesh post code format. Must be 4 digits (e.g., 1200, 1212)'
      };
    }
    return { isValid: true, error: null };
  }

  // If country is not UK or Bangladesh, allow any format (no validation)
  return { isValid: true, error: null };
};

/**
 * Validates post code using web API based on country (async)
 * @param {string} postCode - The post code to validate
 * @param {string|number} countryId - The country ID (22 for UK)
 * @param {Array} countries - Array of country objects with id and name
 * @returns {Promise<Object>} { isValid: boolean, error: string|null, data: object|null }
 */
export const validatePostCodeFromWeb = async (postCode, countryId, countries = []) => {
  if (!postCode || !postCode.trim()) {
    return { isValid: false, error: 'Post code is required', data: null };
  }

  // Find country by ID
  const country = countries.find(c => String(c.id) === String(countryId));
  const countryName = country?.name?.toLowerCase() || '';

  // UK validation using web API
  if (countryId === 22 || countryId === '22' || countryName === 'uk') {
    return await validateUKPostCodeFromWeb(postCode);
  }

  // Bangladesh validation using web API
  if (countryName.includes('bangladesh') || countryName === 'bd') {
    return await validateBangladeshPostCodeFromWeb(postCode);
  }

  // If country is not UK or Bangladesh, allow any format (no validation)
  return { isValid: true, error: null, data: null };
};

/**
 * Formats UK post code with proper spacing
 * @param {string} postCode - The post code to format
 * @returns {string} - Formatted post code
 */
export const formatUKPostCode = (postCode) => {
  if (!postCode) return '';
  
  // Remove all spaces and convert to uppercase
  const cleaned = postCode.replace(/\s+/g, '').toUpperCase();
  
  // If it matches UK format, add space before last 3 characters
  if (/^[A-Z]{1,2}[0-9][A-Z0-9]?[0-9][A-Z]{2}$/.test(cleaned)) {
    return cleaned.slice(0, -3) + ' ' + cleaned.slice(-3);
  }
  
  return postCode.toUpperCase();
};

/**
 * Validates and formats post code based on country (synchronous)
 * @param {string} postCode - The post code to validate and format
 * @param {string|number} countryId - The country ID
 * @param {Array} countries - Array of country objects
 * @returns {Object} { isValid: boolean, error: string|null, formatted: string }
 */
export const validateAndFormatPostCode = (postCode, countryId, countries = []) => {
  const validation = validatePostCode(postCode, countryId, countries);
  
  if (!validation.isValid) {
    return { ...validation, formatted: postCode };
  }

  // Format UK post codes
  const country = countries.find(c => String(c.id) === String(countryId));
  const countryName = country?.name?.toLowerCase() || '';
  
  if (countryId === 22 || countryId === '22' || countryName === 'uk') {
    return {
      ...validation,
      formatted: formatUKPostCode(postCode)
    };
  }

  return { ...validation, formatted: postCode };
};

/**
 * Validates and formats post code using web API (async)
 * @param {string} postCode - The post code to validate and format
 * @param {string|number} countryId - The country ID
 * @param {Array} countries - Array of country objects
 * @returns {Promise<Object>} { isValid: boolean, error: string|null, formatted: string, data: object|null }
 */
export const validateAndFormatPostCodeFromWeb = async (postCode, countryId, countries = []) => {
  const validation = await validatePostCodeFromWeb(postCode, countryId, countries);
  
  if (!validation.isValid) {
    return { ...validation, formatted: postCode };
  }

  // Format UK post codes
  const country = countries.find(c => String(c.id) === String(countryId));
  const countryName = country?.name?.toLowerCase() || '';
  
  if (countryId === 22 || countryId === '22' || countryName === 'uk') {
    // Use formatted postcode from API if available, otherwise format manually
    const formatted = validation.data?.postcode || formatUKPostCode(postCode);
    return {
      ...validation,
      formatted: formatted
    };
  }

  return { ...validation, formatted: postCode };
};

